import React, { useState } from 'react';
import { Building2, Book, FileText, Calendar, CheckCircle, Download, Users, Target, Award, TrendingUp, Clock, DollarSign, Briefcase, GraduationCap, AlertCircle, BarChart, BookOpen, PenTool, Brain, Globe, Calculator, Landmark } from 'lucide-react';
import { FaBuilding, FaBusinessTime } from 'react-icons/fa';

const BankPOClerkPage = () => {
  const [selectedExam, setSelectedExam] = useState('IBPS_PO');

  const examCategories = {
    'IBPS_PO': {
      name: "IBPS PO - Probationary Officer",
      fullName: "Institute of Banking Personnel Selection - Probationary Officer",
      posts: "3,000+ Vacancies",
      salary: "₹50,000 - ₹60,000 per month",
      scale: "Scale I Officer",
      icon: <Landmark />,
      color: "#667eea",
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
    },
    'SBI_PO': {
      name: "SBI PO - Probationary Officer",
      fullName: "State Bank of India - Probationary Officer",
      posts: "2,000+ Vacancies",
      salary: "₹57,000 - ₹70,000 per month",
      scale: "JMGS-I (Junior Management Grade Scale-I)",
      icon: <Award />,
      color: "#f093fb",
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
    },
    'IBPS_Clerk': {
      name: "IBPS Clerk",
      fullName: "Institute of Banking Personnel Selection - Clerk",
      posts: "7,000+ Vacancies",
      salary: "₹25,000 - ₹35,000 per month",
      scale: "Clerical Cadre",
      icon: <BookOpen />,
      color: "#43e97b",
      gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
    },
    'SBI_Clerk': {
      name: "SBI Clerk",
      fullName: "State Bank of India - Junior Associate",
      posts: "5,000+ Vacancies",
      salary: "₹31,000 - ₹42,000 per month",
      scale: "Junior Associate (Customer Support & Sales)",
      icon: <Users />,
      color: "#fa709a",
      gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)"
    }
  };

  const examPattern = {
    IBPS_PO: {
      prelims: {
        name: "Prelims Examination",
        duration: "60 Minutes",
        totalMarks: 100,
        sections: [
          { subject: "English Language", questions: 30, marks: 30, time: "20 min" },
          { subject: "Quantitative Aptitude", questions: 35, marks: 35, time: "20 min" },
          { subject: "Reasoning Ability", questions: 35, marks: 35, time: "20 min" }
        ],
        negativeMarking: "0.25 marks deducted per wrong answer"
      },
      mains: {
        name: "Mains Examination",
        duration: "180 Minutes",
        totalMarks: 200,
        sections: [
          { subject: "Reasoning & Computer Aptitude", questions: 45, marks: 60, time: "60 min" },
          { subject: "General Economy & Banking Awareness", questions: 40, marks: 40, time: "35 min" },
          { subject: "English Language", questions: 35, marks: 40, time: "40 min" },
          { subject: "Data Analysis & Interpretation", questions: 35, marks: 60, time: "45 min" }
        ],
        negativeMarking: "0.25 marks deducted per wrong answer"
      },
      interview: {
        name: "Personal Interview",
        marks: 100,
        description: "Final selection based on Mains + Interview (80:20 ratio)"
      }
    },
    SBI_PO: {
      prelims: {
        name: "Prelims Examination",
        duration: "60 Minutes",
        totalMarks: 100,
        sections: [
          { subject: "English Language", questions: 30, marks: 30, time: "20 min" },
          { subject: "Quantitative Aptitude", questions: 35, marks: 35, time: "20 min" },
          { subject: "Reasoning Ability", questions: 35, marks: 35, time: "20 min" }
        ],
        negativeMarking: "0.25 marks deducted per wrong answer"
      },
      mains: {
        name: "Mains Examination",
        duration: "180 Minutes",
        totalMarks: 250,
        sections: [
          { subject: "Reasoning & Computer Aptitude", questions: 45, marks: 60, time: "60 min" },
          { subject: "Data Analysis & Interpretation", questions: 35, marks: 60, time: "45 min" },
          { subject: "General/Economy/Banking Awareness", questions: 40, marks: 40, time: "35 min" },
          { subject: "English Language", questions: 35, marks: 40, time: "40 min" }
        ],
        descriptive: "Letter Writing & Essay (50 marks in 30 minutes)",
        negativeMarking: "0.25 marks deducted per wrong answer"
      },
      gd: {
        name: "Group Discussion",
        marks: 20,
        description: "Topic-based group discussion"
      },
      interview: {
        name: "Personal Interview",
        marks: 30,
        description: "Final selection: Mains + GD + Interview"
      }
    },
    IBPS_Clerk: {
      prelims: {
        name: "Prelims Examination",
        duration: "60 Minutes",
        totalMarks: 100,
        sections: [
          { subject: "English Language", questions: 30, marks: 30, time: "20 min" },
          { subject: "Numerical Ability", questions: 35, marks: 35, time: "20 min" },
          { subject: "Reasoning Ability", questions: 35, marks: 35, time: "20 min" }
        ],
        negativeMarking: "0.25 marks deducted per wrong answer"
      },
      mains: {
        name: "Mains Examination",
        duration: "160 Minutes",
        totalMarks: 200,
        sections: [
          { subject: "Reasoning Ability & Computer Aptitude", questions: 50, marks: 60, time: "45 min" },
          { subject: "English Language", questions: 40, marks: 40, time: "35 min" },
          { subject: "Quantitative Aptitude", questions: 50, marks: 50, time: "45 min" },
          { subject: "General/Financial Awareness", questions: 50, marks: 50, time: "35 min" }
        ],
        negativeMarking: "0.25 marks deducted per wrong answer"
      }
    },
    SBI_Clerk: {
      prelims: {
        name: "Prelims Examination",
        duration: "60 Minutes",
        totalMarks: 100,
        sections: [
          { subject: "English Language", questions: 30, marks: 30, time: "20 min" },
          { subject: "Numerical Ability", questions: 35, marks: 35, time: "20 min" },
          { subject: "Reasoning Ability", questions: 35, marks: 35, time: "20 min" }
        ],
        negativeMarking: "0.25 marks deducted per wrong answer"
      },
      mains: {
        name: "Mains Examination",
        duration: "160 Minutes",
        totalMarks: 200,
        sections: [
          { subject: "General/Financial Awareness", questions: 50, marks: 50, time: "35 min" },
          { subject: "General English", questions: 40, marks: 40, time: "35 min" },
          { subject: "Quantitative Aptitude", questions: 50, marks: 50, time: "45 min" },
          { subject: "Reasoning Ability & Computer Aptitude", questions: 60, marks: 60, time: "45 min" }
        ],
        negativeMarking: "0.25 marks deducted per wrong answer"
      }
    }
  };

  const syllabus = {
    "Quantitative Aptitude": [
      "Number System & Simplification",
      "Average, Percentage, Ratio & Proportion",
      "Profit & Loss, Simple & Compound Interest",
      "Time & Work, Time & Distance",
      "Data Interpretation: Tables, Bar Graphs, Pie Charts, Line Graphs",
      "Data Sufficiency",
      "Quadratic Equations",
      "Mensuration & Geometry",
      "Permutation & Combination",
      "Probability",
      "Sequence & Series",
      "Algebra: Linear Equations"
    ],
    "Reasoning Ability": [
      "Syllogism & Statements",
      "Coding-Decoding",
      "Seating Arrangement: Linear, Circular, Box-Based",
      "Puzzle: Floor-Based, Schedule-Based",
      "Blood Relations",
      "Direction Sense",
      "Order & Ranking",
      "Inequality",
      "Input-Output",
      "Data Sufficiency",
      "Alphanumeric Series",
      "Miscellaneous Reasoning"
    ],
    "English Language": [
      "Reading Comprehension",
      "Cloze Test (Fill in the Blanks)",
      "Para Jumbles",
      "Sentence Improvement",
      "Error Spotting",
      "Sentence Completion",
      "Vocabulary: Synonyms & Antonyms",
      "Phrase Replacement",
      "Active & Passive Voice",
      "Direct & Indirect Speech",
      "Idioms & Phrases",
      "Grammar Rules"
    ],
    "General Awareness": [
      "Banking & Financial Awareness",
      "Current Affairs: Last 6 Months",
      "Banking Terms & Definitions",
      "RBI Functions & Monetary Policy",
      "Budget & Economic Survey",
      "Government Schemes",
      "Important Days & Events",
      "Books & Authors",
      "Awards & Honours",
      "Sports: National & International",
      "Agreements & Summits",
      "Static GK: Capitals, Currencies, International Organizations"
    ],
    "Computer Awareness": [
      "Computer Fundamentals",
      "MS Office: Word, Excel, PowerPoint",
      "Internet & Email",
      "Computer Networks",
      "Operating Systems",
      "Database Management",
      "Computer Security & Virus",
      "Computer Abbreviations",
      "Latest Technology Trends",
      "Digital Banking",
      "Payment Systems: UPI, NEFT, RTGS, IMPS",
      "Cyber Security"
    ]
  };

  const benefits = [
    {
      icon: <DollarSign />,
      title: "Attractive Salary",
      desc: "₹25,000 to ₹70,000 per month with allowances"
    },
    {
      icon: <Award />,
      title: "Job Security",
      desc: "Permanent government job with pension benefits"
    },
    {
      icon: <TrendingUp />,
      title: "Career Growth",
      desc: "Regular promotions and pay scale increments"
    },
    {
      icon: <Briefcase />,
      title: "Work-Life Balance",
      desc: "Fixed working hours with weekends off"
    },
    {
      icon: <Users />,
      title: "Social Prestige",
      desc: "High respect in society and family"
    },
    {
      icon: <Building2 />,
      title: "Perks & Benefits",
      desc: "Housing, medical, travel allowances & more"
    }
  ];

  const participatingBanks = [
    "State Bank of India (SBI)",
    "Punjab National Bank (PNB)",
    "Bank of Baroda",
    "Canara Bank",
    "Union Bank of India",
    "Bank of India",
    "Indian Bank",
    "Central Bank of India",
    "Indian Overseas Bank",
    "UCO Bank",
    "Bank of Maharashtra",
    "Punjab & Sind Bank",
    "IDBI Bank",
    "Axis Bank",
    "ICICI Bank",
    "HDFC Bank",
    "Kotak Mahindra Bank",
    "Yes Bank",
    "IndusInd Bank",
    "Federal Bank"
  ];

  const preparationTips = [
    {
      icon: <Book />,
      title: "Master Basics",
      desc: "Build strong foundation in Quant, Reasoning & English"
    },
    {
      icon: <Clock />,
      title: "Time Management",
      desc: "Practice sectional timing to complete exam on time"
    },
    {
      icon: <PenTool />,
      title: "Mock Tests",
      desc: "Solve 100+ mock tests before actual exam"
    },
    {
      icon: <Globe />,
      title: "Current Affairs",
      desc: "Read daily newspapers and banking magazines"
    },
    {
      icon: <Brain />,
      title: "Speed & Accuracy",
      desc: "Focus on solving questions quickly without errors"
    },
    {
      icon: <FileText />,
      title: "Previous Papers",
      desc: "Analyze last 5 years question papers thoroughly"
    }
  ];

  const importantDates = {
    IBPS_PO: [
      { event: "Notification Release", date: "August", status: "Official" },
      { event: "Online Application", date: "August - September", status: "Apply" },
      { event: "Prelims Admit Card", date: "September", status: "Download" },
      { event: "Prelims Exam", date: "October", status: "Tier 1" },
      { event: "Prelims Result", date: "October", status: "Check" },
      { event: "Mains Admit Card", date: "November", status: "Download" },
      { event: "Mains Exam", date: "November", status: "Tier 2" },
      { event: "Mains Result", date: "December", status: "Check" },
      { event: "Interview", date: "January - February", status: "Final" },
      { event: "Final Result", date: "March", status: "Selection" }
    ],
    SBI_PO: [
      { event: "Notification Release", date: "December - January", status: "Official" },
      { event: "Online Application", date: "January", status: "Apply" },
      { event: "Prelims Admit Card", date: "March", status: "Download" },
      { event: "Prelims Exam", date: "April", status: "Tier 1" },
      { event: "Prelims Result", date: "May", status: "Check" },
      { event: "Mains Admit Card", date: "May", status: "Download" },
      { event: "Mains Exam", date: "June", status: "Tier 2" },
      { event: "Mains Result", date: "July", status: "Check" },
      { event: "GD & Interview", date: "August - September", status: "Final" },
      { event: "Final Result", date: "October", status: "Selection" }
    ],
    IBPS_Clerk: [
      { event: "Notification Release", date: "July", status: "Official" },
      { event: "Online Application", date: "July - August", status: "Apply" },
      { event: "Prelims Admit Card", date: "November", status: "Download" },
      { event: "Prelims Exam", date: "December", status: "Tier 1" },
      { event: "Prelims Result", date: "December", status: "Check" },
      { event: "Mains Admit Card", date: "January", status: "Download" },
      { event: "Mains Exam", date: "January", status: "Tier 2" },
      { event: "Final Result", date: "March", status: "Selection" }
    ],
    SBI_Clerk: [
      { event: "Notification Release", date: "January", status: "Official" },
      { event: "Online Application", date: "January - February", status: "Apply" },
      { event: "Prelims Admit Card", date: "April", status: "Download" },
      { event: "Prelims Exam", date: "May", status: "Tier 1" },
      { event: "Prelims Result", date: "June", status: "Check" },
      { event: "Mains Admit Card", date: "June", status: "Download" },
      { event: "Mains Exam", date: "July", status: "Tier 2" },
      { event: "Final Result", date: "September", status: "Selection" }
    ]
  };

  const eligibility = {
    age: {
      PO: "20-30 years (5 years relaxation for SC/ST, 3 years for OBC)",
      Clerk: "20-28 years (5 years relaxation for SC/ST, 3 years for OBC)"
    },
    qualification: {
      PO: "Graduate degree in any discipline from recognized university",
      Clerk: "Graduate degree in any discipline from recognized university"
    },
    fee: {
      general: "₹850 (PO) / ₹750 (Clerk)",
      scst: "₹175 (Application fee only, Exam fee exempted)"
    }
  };

  return (
    <div style={{ background: '#f8f9fa', minHeight: '100vh', marginTop:'30px' }}>
      {/* Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, #0089a8ff 0%, #6204c1ff 50%, #000000ff 100%)',
        padding: '80px 20px 60px',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ fontSize: '4rem', marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
            <FaBusinessTime size={80} />
          </div>
          <h1 style={{ fontSize: '3rem', fontWeight: 'bold', marginBottom: '20px' }}>
            Bank PO & Clerk Exam 2025
          </h1>
          <p style={{ fontSize: '1.4rem', marginBottom: '15px', opacity: 0.95 }}>
            IBPS & SBI Recruitment - Probationary Officers & Clerks
          </p>
          <p style={{ fontSize: '1.2rem', marginBottom: '30px', opacity: 0.9 }}>
            17,000+ Vacancies | Public & Private Sector Banks
          </p>
          <div style={{
            background: 'rgba(255,255,255,0.2)',
            padding: '20px 25px',
            borderRadius: '10px',
            display: 'inline-block',
            marginBottom: '30px',
            maxWidth: '900px'
          }}>
            <p style={{ fontSize: '1.1rem', margin: 0, lineHeight: '1.6' }}>
              <strong>💼 PO Salary:</strong> ₹50,000 - ₹70,000/month | <strong>Clerk Salary:</strong> ₹25,000 - ₹42,000/month<br/>
              <strong>🎯 Exams:</strong> IBPS PO, SBI PO, IBPS Clerk, SBI Clerk
            </p>
          </div>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => alert('Enrollment functionality')}
              style={{
                background: 'white',
                color: '#667eea',
                padding: '15px 40px',
                borderRadius: '30px',
                border: 'none',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-3px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              Start Banking Exam Preparation
            </button>
            <button
              onClick={() => document.getElementById('syllabus').scrollIntoView({ behavior: 'smooth' })}
              style={{
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                padding: '15px 40px',
                borderRadius: '30px',
                border: '2px solid white',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = '#667eea';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                e.currentTarget.style.color = 'white';
              }}
            >
              View Complete Syllabus
            </button>
          </div>
        </div>
      </div>

      {/* Exam Selection Tabs */}
      <div style={{ 
        background: 'white', 
        padding: '30px 20px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
        position: 'relative',
        top: '0',
        zIndex: 100
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '20px'
          }}>
            {Object.keys(examCategories).map(exam => (
              <button
                key={exam}
                onClick={() => setSelectedExam(exam)}
                style={{
                  padding: '25px',
                  borderRadius: '12px',
                  border: 'none',
                  background: selectedExam === exam 
                    ? examCategories[exam].gradient
                    : '#f0f0f0',
                  color: selectedExam === exam ? 'white' : '#333',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: selectedExam === exam ? `0 4px 15px ${examCategories[exam].color}40` : 'none',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => {
                  if (selectedExam !== exam) {
                    e.currentTarget.style.background = '#e0e0e0';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedExam !== exam) {
                    e.currentTarget.style.background = '#f0f0f0';
                  }
                }}
              >
                <div style={{ fontSize: '2.5rem', marginBottom: '15px', display: 'flex' }}>
                  {examCategories[exam].icon}
                </div>
                <div style={{ fontSize: '1.3rem', marginBottom: '8px' }}>
                  {examCategories[exam].name}
                </div>
                <div style={{ fontSize: '0.95rem', opacity: selectedExam === exam ? 0.9 : 0.7 }}>
                  {examCategories[exam].posts}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 20px' }}>
        
        {/* Exam Overview */}
        <section style={{ marginBottom: '60px' }}>
          <div style={{
            background: 'white',
            padding: '40px',
            borderRadius: '20px',
            boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
            border: `3px solid ${examCategories[selectedExam].color}`
          }}>
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <div style={{ 
                fontSize: '3.5rem', 
                color: examCategories[selectedExam].color,
                marginBottom: '20px',
                display: 'flex',
                justifyContent: 'center'
              }}>
                {examCategories[selectedExam].icon}
              </div>
              
              <h2 style={{ 
                fontSize: '2.5rem', 
                fontWeight: 'bold', 
                marginBottom: '10px',
                color: '#2c3e50'
              }}>
                {examCategories[selectedExam].name}
              </h2>
              
              <p style={{ 
                fontSize: '1.2rem',
                color: '#666',
                marginBottom: '10px'
              }}>
                {examCategories[selectedExam].fullName}
              </p>

              <p style={{ 
                fontSize: '1.3rem',
                color: examCategories[selectedExam].color,
                fontWeight: '600',
                marginTop: '20px'
              }}>
                {examCategories[selectedExam].salary}
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '25px'
            }}>
              <div style={{
                padding: '25px',
                background: `${examCategories[selectedExam].color}10`,
                borderRadius: '15px',
                border: `2px solid ${examCategories[selectedExam].color}30`
              }}>
                <h3 style={{ 
                  fontSize: '1.3rem', 
                  fontWeight: 'bold', 
                  marginBottom: '15px',
                  color: examCategories[selectedExam].color,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <Briefcase size={24} />
                  Vacancies
                </h3>
                <p style={{ 
                  fontSize: '1.8rem',
                  fontWeight: 'bold',
                  color: '#2c3e50',
                  margin: 0
                }}>
                  {examCategories[selectedExam].posts}
                </p>
              </div>

              <div style={{
                padding: '25px',
                background: '#e3f2fd',
                borderRadius: '15px',
                border: '2px solid #2196f3'
              }}>
                <h3 style={{ 
                  fontSize: '1.3rem', 
                  fontWeight: 'bold', 
                  marginBottom: '15px',
                  color: '#1565c0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <Award size={24} />
                  Pay Scale
                </h3>
                <p style={{ 
                  fontSize: '1.05rem',
                  color: '#555',
                  margin: 0,
                  lineHeight: '1.6'
                }}>
                  {examCategories[selectedExam].scale}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            Why Choose Banking Career?
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '30px'
          }}>
            {benefits.map((benefit, idx) => (
              <div key={idx} style={{
                background: 'white',
                padding: '35px',
                borderRadius: '15px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                border: '2px solid transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-10px)';
                e.currentTarget.style.boxShadow = `0 12px 30px ${examCategories[selectedExam].color}30`;
                e.currentTarget.style.borderColor = examCategories[selectedExam].color;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)';
                e.currentTarget.style.borderColor = 'transparent';
              }}>
                <div style={{ 
                  fontSize: '3.5rem',
                  color: examCategories[selectedExam].color,
                  marginBottom: '20px',
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  {benefit.icon}
                </div>
                <h3 style={{ 
                  fontSize: '1.4rem',
                  fontWeight: 'bold',
                  marginBottom: '15px',
                  color: '#2c3e50'
                }}>
                  {benefit.title}
                </h3>
                <p style={{ 
                  color: '#666',
                  lineHeight: '1.6',
                  margin: 0,
                  fontSize: '1rem'
                }}>
                  {benefit.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Exam Pattern */}
        <section style={{ marginBottom: '60px' }}>
                 <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px'
          }}>
            <Award size={32} style={{ color: examCategories[selectedExam].color }} />
            Banking Exam Preparation Courses
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '30px'
          }}>
            {[
              {
                name: "Foundation Course",
                price: "5,999",
                duration: "4 Months",
                features: [
                  "Complete Syllabus Coverage",
                  "Video Lectures (100+ Hours)",
                  "Study Material (PDF)",
                  "20 Mock Tests",
                  "Current Affairs (Daily)",
                  "Doubt Clearing Sessions"
                ],
                gradient: examCategories[selectedExam].gradient
              },
              {
                name: "Premium Course",
                price: "12,999",
                duration: "6 Months",
                features: [
                  "Everything in Foundation",
                  "Live Interactive Classes",
                  "50+ Full-Length Mock Tests",
                  "Previous 5 Years Papers",
                  "Sectional Tests (200+)",
                  "Banking Awareness Classes",
                  "Personal Mentorship",
                  "Interview Preparation"
                ],
                gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                popular: true
              },
              {
                name: "Complete Package",
                price: "19,999",
                duration: "12 Months",
                features: [
                  "Everything in Premium",
                  "One-on-One Mentoring",
                  "Unlimited Mock Tests",
                  "All Banking Exams Coverage",
                  "Descriptive Writing Classes",
                  "GD & Interview Training",
                  "Performance Analytics",
                  "Study Planner & Tracker",
                  "24/7 Premium Support"
                ],
                gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
              }
            ].map((pkg, idx) => (
              <div key={idx} style={{
                background: 'white',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: pkg.popular ? '0 12px 40px rgba(102,126,234,0.3)' : '0 4px 15px rgba(0,0,0,0.08)',
                border: pkg.popular ? '3px solid #667eea' : '1px solid #e8e8e8',
                position: 'relative',
                transform: pkg.popular ? 'scale(1.05)' : 'scale(1)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = pkg.popular ? 'scale(1.08)' : 'scale(1.03)';
                e.currentTarget.style.boxShadow = '0 15px 45px rgba(102,126,234,0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = pkg.popular ? 'scale(1.05)' : 'scale(1)';
                e.currentTarget.style.boxShadow = pkg.popular ? '0 12px 40px rgba(102,126,234,0.3)' : '0 4px 15px rgba(0,0,0,0.08)';
              }}>
                {pkg.popular && (
                  <div style={{
                    position: 'absolute',
                    top: '20px',
                    right: '-35px',
                    background: '#FFD700',
                    color: '#333',
                    padding: '5px 40px',
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    transform: 'rotate(45deg)',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                    zIndex: 1
                  }}>
                    RECOMMENDED
                  </div>
                )}
                
                <div style={{
                  background: pkg.gradient,
                  padding: '40px 30px',
                  textAlign: 'center',
                  color: 'white'
                }}>
                  <h3 style={{ 
                    fontSize: '1.8rem',
                    fontWeight: 'bold',
                    marginBottom: '15px'
                  }}>
                    {pkg.name}
                  </h3>
                  <div style={{ fontSize: '3rem', fontWeight: 'bold', marginBottom: '10px' }}>
                    ₹{pkg.price}
                  </div>
                  <div style={{ fontSize: '1.1rem', opacity: 0.9 }}>
                    {pkg.duration}
                  </div>
                </div>

                <div style={{ padding: '30px' }}>
                  <ul style={{ 
                    listStyle: 'none',
                    padding: 0,
                    margin: '0 0 25px 0'
                  }}>
                    {pkg.features.map((feature, i) => (
                      <li key={i} style={{
                        padding: '12px 0',
                        borderBottom: i < pkg.features.length - 1 ? '1px solid #f0f0f0' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        color: '#555'
                      }}>
                        <CheckCircle size={16} style={{ color: '#43e97b', flexShrink: 0 }} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button style={{
                    width: '100%',
                    padding: '15px',
                    borderRadius: '10px',
                    border: 'none',
                    background: pkg.gradient,
                    color: 'white',
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => alert('Enrollment functionality')}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}>
                    Enroll Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQs */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            Frequently Asked Questions
          </h2>

          <div style={{
            background: 'white',
            borderRadius: '15px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
            overflow: 'hidden'
          }}>
            {[
              {
                q: "What is the difference between IBPS PO and SBI PO?",
                a: "IBPS PO is conducted by IBPS for recruitment in multiple public sector banks, while SBI PO is conducted exclusively by State Bank of India. SBI PO offers higher salary (₹57,000-70,000) compared to IBPS PO (₹50,000-60,000). SBI PO includes a Group Discussion round, while IBPS PO directly moves to interview after Mains."
              },
              {
                q: "Can I apply for both PO and Clerk exams?",
                a: "Yes, you can apply for both PO and Clerk positions. There is no restriction. However, check the exam dates to ensure there are no clashes. Many candidates prepare for both exams simultaneously as the syllabus is similar."
              },
              {
                q: "What is the salary for Bank PO and Clerk?",
                a: "Bank PO salary ranges from ₹50,000 to ₹70,000 per month (IBPS PO: ₹50,000-60,000, SBI PO: ₹57,000-70,000). Bank Clerk salary ranges from ₹25,000 to ₹42,000 per month (IBPS Clerk: ₹25,000-35,000, SBI Clerk: ₹31,000-42,000). Both include DA, HRA, and other allowances."
              },
              {
                q: "How many attempts are allowed for banking exams?",
                a: "There is no limit on the number of attempts for banking exams. You can appear as many times as you want until you reach the maximum age limit (30 years for PO, 28 years for Clerk for General category)."
              },
              {
                q: "Is there negative marking in banking exams?",
                a: "Yes, there is negative marking of 0.25 marks for each wrong answer in both Prelims and Mains examinations. It's crucial to attempt questions carefully and avoid random guessing."
              },
              {
                q: "How long does it take to prepare for Bank PO exam?",
                a: "With dedicated preparation, 4-6 months is sufficient for Bank PO. However, it depends on your current knowledge level. Beginners may need 6-8 months, while those with strong basics can prepare in 3-4 months. Consistent daily study of 6-8 hours is recommended."
              },
              {
                q: "Which is easier - Bank PO or Bank Clerk?",
                a: "Bank Clerk exam is comparatively easier than Bank PO in terms of difficulty level and competition. However, Clerk posts offer lower salary and fewer career growth opportunities compared to PO positions. Many candidates start with Clerk and later appear for PO exams."
              },
              {
                q: "What books should I refer for banking exam preparation?",
                a: "Recommended books include: Quantitative Aptitude by R.S. Aggarwal, English by Neetu Singh/Wren & Martin, Reasoning by R.S. Aggarwal, Banking Awareness by Arihant/Disha publications, and previous year papers. Regular practice from online test series is also essential."
              },
              {
                q: "Can final year students apply for banking exams?",
                a: "Yes, final year students can apply for banking exams. However, you must have completed your graduation before the final joining date. The degree certificate must be submitted during document verification."
              },
              {
                q: "What is the career growth in banking sector?",
                a: "Banking offers excellent career growth. PO can be promoted to Assistant Manager, Manager, Senior Manager, Chief Manager, and eventually to General Manager level. Regular promotions occur every 3-5 years based on performance. The sector offers job security, good salary increments, and prestigious positions."
              }
            ].map((faq, idx) => (
              <div key={idx} style={{
                padding: '30px',
                borderBottom: idx < 9 ? '1px solid #e8e8e8' : 'none'
              }}>
                <h4 style={{ 
                  fontSize: '1.3rem',
                  fontWeight: 'bold',
                  marginBottom: '15px',
                  color: examCategories[selectedExam].color,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px'
                }}>
                  <span style={{ flexShrink: 0 }}>Q{idx + 1}.</span>
                  <span>{faq.q}</span>
                </h4>
                <p style={{ 
                  color: '#666',
                  lineHeight: '1.8',
                  margin: '0 0 0 35px',
                  fontSize: '1.05rem'
                }}>
                  {faq.a}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section>
          <div style={{
            background: examCategories[selectedExam].gradient,
            padding: '60px 40px',
            borderRadius: '20px',
            textAlign: 'center',
            color: 'white'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
              <Building2 size={64} />
            </div>
            <h2 style={{ 
              fontSize: '2.5rem',
              fontWeight: 'bold',
              marginBottom: '20px'
            }}>
              Start Your Banking Career Today!
            </h2>
            <p style={{ 
              fontSize: '1.3rem',
              marginBottom: '35px',
              opacity: 0.95,
              maxWidth: '700px',
              margin: '0 auto 35px'
            }}>
              Join thousands of successful candidates and secure your dream job in the banking sector
            </p>
            <div style={{ 
              display: 'flex',
              gap: '20px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button 
                onClick={() => alert('Enrollment functionality')}
                style={{
                  background: 'white',
                  color: examCategories[selectedExam].color,
                  padding: '18px 45px',
                  borderRadius: '30px',
                  border: 'none',
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 12px 30px rgba(0,0,0,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.2)';
                }}>
                Start Banking Preparation Now
              </button>
              <button style={{
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                padding: '18px 45px',
                borderRadius: '30px',
                border: '2px solid white',
                fontSize: '1.2rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onClick={() => alert('Download functionality')}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = examCategories[selectedExam].color;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                e.currentTarget.style.color = 'white';
              }}>
                Download Free Mock Tests
              </button>
            </div>
          </div>
        </section>
      </div>

      {/* Footer Note */}
      <div style={{
        background: '#2c3e50',
        color: 'white',
        padding: '30px 20px',
        textAlign: 'center',
        marginTop: '60px'
      }}>
        <p style={{ margin: '0 0 10px', fontSize: '1.1rem' }}>
          © 2025 Olewin Academy - All Rights Reserved
        </p>
        <p style={{ margin: 0, opacity: 0.8 }}>
          For official information, visit: <a href="https://www.ibps.in" target="_blank" rel="noopener noreferrer" style={{ color: '#43e97b', textDecoration: 'none', marginRight: '20px' }}>IBPS Official</a>
          <a href="https://sbi.co.in/careers" target="_blank" rel="noopener noreferrer" style={{ color: '#43e97b', textDecoration: 'none' }}>SBI Careers</a>
        </p>
      </div>
    </div>
  );
};

export default BankPOClerkPage;
          